import type { JSX } from 'react';
import type { Locale } from '../i18n';
import { errorCopy, widgetText } from './copy';
import { ALERT_CLASS, ALERT_DETAIL_CLASS } from './styles';

export interface ErrorMessageProps {
  /** Miembro de extension `code` del problem+json: el contrato estable por el que decide la UI. */
  code: string;
  /**
   * Copia de respaldo del PRIMER renglon, ya localizada por quien llama. Solo se pinta cuando este
   * build no conoce el `code`; nunca se promueve a detalle tecnico.
   */
  message: string;
  /**
   * `detail` TAL CUAL lo mando el servidor, cuando el fallo viene de la API. Se omite (o llega null)
   * en los fallos que genera el propio widget, que ya nacen localizados en `message`.
   */
  detail?: string | null | undefined;
  locale: Locale;
}

/**
 * Error visible en el checkout. Tiene DOS renglones porque tiene DOS publicos.
 *
 * <p>El primero es para el COMPRADOR y se elige por el `code`, que es el contrato estable: esta
 * traducido y no cambia cuando el backend reescribe un mensaje.
 *
 * <p>El segundo es el `detail` del servidor y es para el COMERCIO. Se ANADE, no sustituye. Antes se
 * descartaba —la linea era `localized ?? message`, y `validation_error` si esta en el diccionario— y
 * con el se descartaba el unico dato que decia que estaba mal: la inscripcion de Oneclick moria con
 * «Los datos del pago no son validos» sin nombrar nunca el campo que faltaba, ni en pantalla ni en
 * ningun otro sitio. Una copia generica con la que no se puede arreglar nada es peor que un mensaje
 * tecnico con el que si.
 *
 * <p><strong>El detalle NO se traduce, a proposito.</strong> Nombra campos del contrato
 * (`customer_email`, `metadata.customer_email`) y traducirlo exigiria un diccionario paralelo de
 * mensajes de servidor que se desincroniza en la primera release del backend; traducir el NOMBRE del
 * campo lo dejaria inservible justo para quien tiene que arreglarlo. Lo que si se hace explicito es el
 * cambio de PUBLICO: una etiqueta localizada delante que dice de quien es el mensaje, para que el
 * comprador lea «esto no va dirigido a mi» sin tener que entenderlo.
 *
 * <p><strong>Y no se marca el idioma, porque no se sabe.</strong> Este span llevaba `lang="en"`
 * afirmando que payment-api redacta los `detail` en ingles, y es falso: lo son los que escriben los
 * servicios a mano, pero `ProblemDetailFactory` rellena con `ProblemCode.defaultDetail()` cuando el
 * llamador no aporta ninguno, y esos textos estan en ESPANOL («El recurso solicitado no existe.»,
 * «La pasarela rechazo el pago.»), igual que los del limitador de peticiones. El contrato no publica
 * en que idioma viene el `detail`, asi que cualquier `lang` fijo miente la mitad de las veces —y
 * marcar mal el idioma es peor que no marcarlo: un lector de pantalla en es-CL leia con fonetica
 * inglesa un mensaje en castellano, que es exactamente el dano que el atributo decia evitar
 * (WCAG 3.1.2). Sin atributo hereda el del documento, que acierta con el caso mas frecuente. Para
 * marcarlo de verdad haria falta que el servidor declarase el idioma del `detail`.
 *
 * <p>Va literal, sin filtrar ni recortar: que el `detail` no lleve datos sensibles es responsabilidad
 * del servidor (CONVENCIONES 3.3 y 3.7), y decidir aqui que parte es publicable seria adivinar sobre
 * un texto que este widget no controla ni conoce.
 */
export function ErrorMessage({ code, message, detail, locale }: ErrorMessageProps): JSX.Element {
  const localized = errorCopy(locale, code);
  const buyerLine = localized ?? message;
  // Con un `code` desconocido `message` ES el detalle del servidor, y repetirlo debajo de si mismo
  // solo hace ruido. La comparacion es el unico caso en que el segundo renglon se calla.
  const serverDetail =
    detail !== undefined && detail !== null && detail.length > 0 && detail !== buyerLine
      ? detail
      : null;

  return (
    <div
      data-testid="error-message"
      data-code={code}
      role="alert"
      aria-live="assertive"
      className={ALERT_CLASS}
    >
      <p className="font-semibold">{widgetText(locale, 'error.title')}</p>
      <p>{buyerLine}</p>
      {serverDetail === null ? null : (
        <p data-testid="error-detail" data-audience="merchant" className={ALERT_DETAIL_CLASS}>
          <span className="font-medium">{widgetText(locale, 'error.detail_label')}</span>{' '}
          <span data-testid="error-detail-text">{serverDetail}</span>
        </p>
      )}
    </div>
  );
}
